import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toStoreCreate } from '../api/mappers';
import { useCreateStore } from '../hooks/use-store-mutations';
import {
  createStoreSchema,
  type CreateStoreInput,
  type CreateStoreOutput,
} from '../schemas/store-form-schema';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const DEFAULTS: CreateStoreInput = {
  name: '',
  slug: '',
  defaultCurrency: 'USD',
  countryCode: 'US',
  timezone: 'America/New_York',
  defaultLocale: 'en-US',
};

/** New stores start as drafts; the settings page activates them. */
/** `tour` marks the header instance the stores tour points at; the empty state has the other. */
export function CreateStoreDialog({ tour = false }: { tour?: boolean }) {
  const { t } = useTranslation(['stores', 'common']);
  const navigate = useNavigate();
  const create = useCreateStore();
  const message = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const form = useForm<CreateStoreInput, unknown, CreateStoreOutput>({
    resolver: useI18nResolver(createStoreSchema),
    defaultValues: DEFAULTS,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const submit = form.handleSubmit(async (values) => {
    try {
      const store = await create.mutateAsync(toStoreCreate(values));
      setOpen(false);
      form.reset(DEFAULTS);
      await navigate({ to: '/org/stores/$storeId', params: { storeId: store.id } });
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  const field = (
    name: 'name' | 'slug' | 'defaultCurrency' | 'countryCode' | 'timezone' | 'defaultLocale',
    extra: { hint?: string; className?: string; onChange?: (value: string) => void } = {},
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field: f }) => (
        <FormItem className={extra.className}>
          <FormLabel>{t(`stores:fields.${name}`)}</FormLabel>
          <FormControl>
            <Input
              {...f}
              onChange={(e) => {
                f.onChange(e);
                extra.onChange?.(e.target.value);
              }}
            />
          </FormControl>
          {extra.hint ? <FormDescription>{extra.hint}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-tour={tour ? 'org.stores.new' : undefined}>
          <Plus aria-hidden />
          {t('stores:create.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('stores:create.title')}</DialogTitle>
          <DialogDescription>{t('stores:create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" className="sm:col-span-2">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            {field('name', {
              className: 'sm:col-span-2',
              onChange: (value) => {
                if (!slugTouched) form.setValue('slug', slugify(value), { shouldDirty: true });
              },
            })}
            {field('slug', {
              className: 'sm:col-span-2',
              hint: t('stores:fields.slugHint'),
              onChange: () => setSlugTouched(true),
            })}
            {field('defaultCurrency')}
            {field('countryCode')}
            {field('timezone')}
            {field('defaultLocale')}
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('common:actions.saving') : t('stores:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
